import React, { FormEvent } from 'react';
import ReactDOM from 'react-dom';
import CenterModal from './CenterModal';
import './GenericModal.scss';

type GenericModalProps = {
  show: boolean;
  close: () => void;
  onSubmit?: (e: FormEvent) => void;
  headerText: string;
  children: any;
  modalBodyClassName?: string;
  dismissButtonText: string;
  confirmButtonText?: string;
  disableConfirm?: boolean;
  customModalClassName?: string;
  customHeaderClassName?: string;
  customFooterClassName?: string;
  customDismissButtonClassName?: string;
  customConfirmButtonClassName?: string;
  hideSubmitButton?: boolean;
  extraButton?: any;
  extraButtonOnClick?: any;
  customExtraButtonClassName?: string;
};

/**
 * Generic component for creating Modals. It has a generic
 * header, a footer with a cancel and a submit button. The
 * CSS classes can be overridden by passing in specific classes
 * for a given attribute.
 * @param show - Boolean value to show/hide the modal
 * @param close - Handle the dismiss of modal
 * @param onSubmit - Handle the click of confirm button
 * @param headerText - Text to display in the header
 * @param children - Body component for the modal
 * @param modalBodyClassName - CSS classname for the body
 * @param dismissButtonText - Text for "cancel" dismiss button
 * @param confirmButtonText - Text for "submit" confirm button
 * @param disableConfirm - Disable confirm button
 * @param customModalClassName - Custom CSS class for the Modal
 * @param customHeaderClassName - Custom CSS class for the header
 * @param customFooterClassName - Custom CSS class for the footer
 * @param customDismissButtonClassName - Custom CSS class for the cancel button
 * @param customConfirmButtonClassName - Custom CSS class for the submit button
 * @param hideSubmitButton - Optional hide of submit button
 * @param extraButton - Optional button that shows up in bottom left corner
 * @param extraButtonOnClick - OnClick for extraButton
 * @param customExtraButtonClassName - Custom CSS for extraButton
 */
export default function GenericModal({
  show,
  close,
  onSubmit = (e) => {
    e.preventDefault();
  },
  headerText,
  children,
  modalBodyClassName = '',
  dismissButtonText,
  confirmButtonText,
  disableConfirm = false,
  customModalClassName = '',
  customHeaderClassName = '',
  customFooterClassName = '',
  customDismissButtonClassName = '',
  customConfirmButtonClassName = '',
  hideSubmitButton = false,
  extraButton = null,
  extraButtonOnClick = null,
  customExtraButtonClassName = '',
}: GenericModalProps) {
  // Main div that renders the contents of a page
  const modalRoot: any = document.getElementById('contents');
  // Create a domNode and add it to the modalRoot
  const domNode: any = document.createElement('div');
  modalRoot.appendChild(domNode);

  return ReactDOM.createPortal(
    <CenterModal show={show} close={close}>
      <form className={'generic-modal ' + customModalClassName} onSubmit={onSubmit}>
        <div className={'generic-modal-header ' + customHeaderClassName}>
          <h1>{headerText}</h1>
        </div>
        <div className={'generic-modal-body ' + modalBodyClassName}>{children}</div>
        <div className={'generic-modal-footer ' + customFooterClassName}>
          {extraButton && (
            <input
              type="button"
              value={extraButton}
              onClick={extraButtonOnClick}
              className={
                'generic-modal-dismiss-btn generic-modal-extra-button-bottom-left ' +
                customExtraButtonClassName
              }
            />
          )}
          <input
            type="button"
            value={dismissButtonText}
            onClick={close}
            className={'generic-modal-dismiss-btn ' + customDismissButtonClassName}
          />
          {!hideSubmitButton && (
            <input
              type="submit"
              value={confirmButtonText}
              disabled={disableConfirm}
              className={'generic-modal-confirm-btn ' + customConfirmButtonClassName}
            />
          )}
        </div>
      </form>
    </CenterModal>,
    domNode
  );
}
